import { ParticipantSearch } from '@/components';
import ContestantView from '@/components/views/Contestants/ContestantView.vue';
import MatrixStationRoleCellTimeEditor from '@/components/views/Matrix/SubComponents/MatrixStationRoleCellEditors/MatrixStationRoleCellTimeEditor.vue';
import { useTaskLoader } from '@/composeables';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';
import { $ti, $tm } from '@/locize';
import type { AddTimeRequest, Participant, RaceContestantDetails, StationRole } from '@/objects/generated';
import { useEventCommonStore } from '@/stores';
import { unref } from 'vue';
import { computed, ref, type Ref } from 'vue';

/**
 * This will open the contestant modal with the appropriate data
 * @remarks if raceContestantId is provided it will load that contestant and ignore the other parameters
 * @param raceContestantId
 * @param raceId
 * @param participant
 * @param openParticipantSearch
 * @param onContestantAdded // Callback when a contestant is added
 */
export const OpenContestantModal = async (params: {
    raceContestantId?: number;
    raceId?: number;
    participant?: Participant;
    openParticipantSearch?: boolean;
    onContestantAdded?: (contestant: RaceContestantDetails | null) => void;
    preFillBib?: number;
    prefillStartTime?: Date;
    // TODO: Add a v-model here (?)
}) => {
    const componentInstance: Ref<InstanceType<typeof ContestantView> | null> = ref(null);

    const { AddLoader, LoadState } = useTaskLoader();

    const dialogData = await useDialogBuilder()
        .withTitle(
            computed(() => {
                if (!componentInstance.value) {
                    return $tm('dashboard.addcontestantsheader');
                }
                return componentInstance.value.title;
            }),
        )
        .withBodyComponent(ContestantView, {
            componentProps: {
                id: params.raceContestantId ?? 0,
                raceId: params.raceId,
                participant: params.participant,
                openParticipantSearch: params.openParticipantSearch,
                preFillBib: params.preFillBib,
                prefillStartTime: params.prefillStartTime,
            },
        })
        .withCloseButton({
            action: () => {},
        })
        .withSaveButton({
            loadstate: LoadState,
            action: async (args) => {
                if (!args.dialogBodyComponentInstanceRef) {
                    return;
                }
                if (!args.dialogBodyComponentInstanceRef.save) {
                    return;
                }
                await args.dialogBodyComponentInstanceRef.save(AddLoader);
                if (params.onContestantAdded) {
                    params.onContestantAdded(unref(args.dialogBodyComponentInstanceRef?.val) ?? null);
                }
            },
        })
        .withOptions({
            Size: 'xxl',
        })
        .Open();

    componentInstance.value = dialogData.dialogBodyComponentInstanceRef || null;
};

/**
 * Opens the add time modal
 * @param params
 */
export const OpenAddTimeModal = (params: {
    raceContestantId?: number;

    contestantId?: number;

    stationRole: StationRole;

    usedTime?: number | null;

    startTime?: Date | null;

    TUID?: string | null;

    bib?: number | null;

    lapNumber?: number;

    /**
     * Callback for when the user saves in the time editor
     *
     * TODO: I dont know if this should have a parameter.
     * Basically this is just useless info, and this cant have any return value given that what we are really doing is just scheduling something to happen in the future
     *
     * @param addTimeRequest The addTimeRequest that was sent to the server
     * @returns
     */
    onTimeAdded?: (addTimeRequest: AddTimeRequest) => void;
}) => {
    useDialogBuilder()
        .withTitle($ti('dashboard.addtimeheader', params.bib ?? 0))
        .withBodyComponent(MatrixStationRoleCellTimeEditor, {
            componentProps: {
                contestantTimeGetter: () => [],
                stationRole: params.stationRole,
                raceContestantId: params.raceContestantId,
                contestantId: params.contestantId ?? null,
                usedTime: params.usedTime ?? null,
                raceContestantStartTime: params.startTime,
                editingTuid: params.TUID ?? null,
                bib: params.bib ?? null,
                LapNumber: params.lapNumber,
                StationIdToBackupStationIdsMap: useEventCommonStore().StationIdToBackupStationIdsMap,
            },
        })
        .withCloseButton({
            action: () => {},
        })
        .withSaveButton({
            action: async (args) => {
                if (!args.dialogBodyComponentInstanceRef) {
                    return;
                }
                const timeRequest = await args.dialogBodyComponentInstanceRef.save();
                if (params.onTimeAdded && timeRequest) {
                    params.onTimeAdded(timeRequest);
                }
            },
        })
        .withOptions({
            Size: 'md',
        })
        .Open();
};

/**
 *
 * @param vModel
 */
export const OpenParticipantSearchModal = async (onSelectedParticipant: (participant: Participant | null) => void) => {
    const vmodel = ref<Participant | null>(null);

    const dialogInstance = await useDialogBuilder()
        .withTitle('racecontestantdetails.selectparticipant')
        .withBodyComponent(ParticipantSearch, {
            vmodel: vmodel,
            componentProps: {
                checkboxSelection: false,
                fullRowSelection: true,
            },
        })
        .withCancelButton({
            action: () => false,
        })
        .withSelectButton({
            action: async (params) => {
                if (params == null || params.dialogBodyComponentInstanceRef == null) {
                    return false;
                }
                onSelectedParticipant(vmodel.value);
                return true;
            },
        })
        .withOptions({
            Size: 'xl',
        })
        .Open();
};
